import React, { useState, useEffect, useCallback, useReducer } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuidv4 } from 'uuid'
import {
	useNavigate,
	useLocation,
} from 'react-router-dom'

import {
	Loader2,
	Check,
	X,
	AlertCircle,
	CheckCircle2,
	HelpCircle,
	Plus,
	Eye,
	Lock,
	EyeIcon,
	RefreshCcw,
} from 'lucide-react'

import { useProgramContext } from '@/context/ProgramContext'
import { apiClient } from '@/lib/api'
import { Column, ColumnManager } from './ColumnManager'
import { PublicUrlDisplay } from './PublicUrlDisplay'
import { LogoUpload } from './LogoUpload'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import useDebounce from '@/hooks/useDebounce'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DataLoadingGuard } from '@/components/DataLoadingGuard'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useTranslation } from '@/hooks/useTranslation'

interface Roadmap {
	id: string
	name: string
	slug: string
	description?: string | null
	is_public: boolean
	logo_url?: string | null
	roadmap_columns: Column[]
}

type ValidationState = 'idle' | 'checking' | 'valid' | 'invalid' | 'short'

// Helper to generate a slug from a name
const generateSlug = (name: string) => {
	return name
		.toLowerCase()
		.trim()
		.replace(/&/g, '-and-') // replace & with 'and'
		.replace(/[\s\W-]+/g, '-') // replace spaces, non-word chars and dashes with a single dash
		.replace(/^-+|-+$/g, '') // remove leading/trailing dashes
}

// Enhanced validation icon component with smooth transitions
const ValidationIcon = ({ state }: { state: ValidationState }) => {
	switch (state) {
		case 'checking':
			return (
				<div className="transition-all duration-200 ease-in-out">
					<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
				</div>
			)
		case 'valid':
			return (
				<div className="transition-all duration-200 ease-in-out animate-in fade-in-0 zoom-in-95">
					<Check className="h-4 w-4 text-emerald-500" />
				</div>
			)
		case 'invalid':
			return (
				<div className="transition-all duration-200 ease-in-out animate-in fade-in-0 zoom-in-95">
					<X className="h-4 w-4 text-red-500" />
				</div>
			)
		default:
			return null
	}
}

const ValidationMessage = ({
	state,
	message,
	t,
}: {
	state: ValidationState
	message?: string
	t: (key: string) => string
}) => {
	if (state === 'idle') return null

	const getIcon = () => {
		switch (state) {
			case 'valid':
				return <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
			case 'invalid':
			case 'short':
				return <AlertCircle className="h-3 w-3 flex-shrink-0" />
			default:
				return null
		}
	}

	const getColorClass = () => {
		switch (state) {
			case 'valid':
				return 'text-emerald-600'
			case 'invalid':
				return 'text-red-600'
			case 'short':
				return 'text-amber-600'
			default:
				return ''
		}
	}

	const defaultMessage = !message && state === 'short'
		? t('roadmapSettings.slugMustBeAtLeast3Characters')
		: message

	return (
		<div className="animate-in slide-in-from-top-1 duration-200 ease-out">
			<p
				className={cn(
					'flex items-start gap-2 text-xs font-medium',
					getColorClass()
				)}>
				{getIcon()}
				<span className="leading-relaxed">{defaultMessage}</span>
			</p>
		</div>
	)
}

const ErrorAlert = ({
	error,
	onRetry,
	t,
}: {
	error: Error
	onRetry: () => void
	t: (key: string) => string
}) => (
	<Alert
		variant="destructive"
		className="animate-in fade-in-0 slide-in-from-bottom-2">
		<AlertCircle className="h-4 w-4" />
		<AlertDescription className="flex items-center justify-between">
			<span>{error.message || t('roadmapSettings.failedToLoadData')}</span>
			<Button variant="outline" size="sm" onClick={onRetry} className="ml-4">
				<RefreshCcw className="h-3 w-3 mr-1" />
				{t('roadmapSettings.retry')}
			</Button>
		</AlertDescription>
	</Alert>
)

// Form state reducer for better state management
interface FormState {
	name: string
	slug: string
	description: string
	isPublic: boolean
	logoUrl: string | null
	columns: Column[]
	showValidation: boolean
}

type FormAction =
	| { type: 'SET_INITIAL_STATE'; payload: Partial<FormState> }
	| { type: 'UPDATE_FIELD'; field: keyof FormState; value: any }
	| { type: 'RESET_VALIDATION' }
	| { type: 'SHOW_VALIDATION' }

const initialFormState: FormState = {
	name: '',
	slug: '',
	description: '',
	isPublic: false,
	logoUrl: null,
	columns: [],
	showValidation: false,
}

function formReducer(state: FormState, action: FormAction): FormState {
	switch (action.type) {
		case 'SET_INITIAL_STATE':
			return { ...state, ...action.payload }
		case 'UPDATE_FIELD':
			return { ...state, [action.field]: action.value }
		case 'RESET_VALIDATION':
			return { ...state, showValidation: false }
		case 'SHOW_VALIDATION':
			return { ...state, showValidation: true }
		default:
			return state
	}
}

// Custom hook for roadmap data fetching with better error handling
function useRoadmapData(programId: string | undefined) {
	return useQuery<Roadmap | null>({
		queryKey: ['roadmap', programId],
		queryFn: async () => {
			if (!programId) {
				throw new Error('Program ID is required')
			}

			try {
				const response = await apiClient(`/api/roadmaps?programId=${programId}`)
				return response || null
			} catch (error: any) {
				// Handle specific error cases
				if (error.status === 404) {
					return null // No roadmap exists yet - this is valid
				}
				throw new Error(error.message || 'Failed to fetch roadmap data')
			}
		},
		enabled: !!programId,
		retry: (failureCount, error: any) => {
			// Don't retry on 404s or client errors
			if (error?.status >= 400 && error?.status < 500) {
				return false
			}
			return failureCount < 2
		},
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
	})
}

function useSlugValidation(slug: string, roadmapId: string | undefined, t: (key: string) => string) {
	const debouncedSlug = useDebounce(slug, 500)

	return useQuery({
		queryKey: ['roadmapSlugCheck', debouncedSlug, roadmapId],
		queryFn: async () => {
			if (!debouncedSlug || debouncedSlug.length < 3) {
				return { available: false, message: t('roadmapSettings.slugTooShort') }
			}

			try {
				const url = `/api/roadmaps/check-slug?slug=${encodeURIComponent(
					debouncedSlug
				)}&currentRoadmapId=${roadmapId || ''}`
				const result = await apiClient(url)
				return result
			} catch (error: any) {
				console.error('Slug validation error:', error)
				return { available: false, message: t('roadmapSettings.failedToValidateSlug') }
			}
		},
		enabled: !!debouncedSlug && debouncedSlug.length >= 3,
		retry: 1,
		staleTime: 30 * 1000, // 30 seconds
	})
}

// Custom hook for form state management and dirty tracking
function useFormState(roadmap: Roadmap | null, isLoading: boolean) {
	const [formState, dispatch] = useReducer(formReducer, initialFormState)
	const [initialState, setInitialState] = useState<Partial<FormState>>({})

	// Initialize form state when roadmap data loads
	useEffect(() => {
		if (isLoading) return // Don't update while loading

		const newInitialState = roadmap
			? {
					name: roadmap.name,
					slug: roadmap.slug,
					description: roadmap.description || '',
					isPublic: roadmap.is_public,
					logoUrl: roadmap.logo_url || null,
					columns: [...(roadmap.roadmap_columns || [])].sort(
						(a, b) => a.order_index - b.order_index
					),
					showValidation: false,
			  }
			: {
					name: '',
					slug: '',
					description: '',
					isPublic: false,
					logoUrl: null,
					columns: [],
					showValidation: false,
			  }

		setInitialState(newInitialState)
		dispatch({ type: 'SET_INITIAL_STATE', payload: newInitialState })
	}, [roadmap, isLoading])

	// Calculate dirty state
	const isDirty = React.useMemo(() => {
		if (!initialState) return false

		return (
			formState.name !== initialState.name ||
			formState.slug !== initialState.slug ||
			formState.description !== initialState.description ||
			formState.isPublic !== initialState.isPublic ||
			formState.logoUrl !== initialState.logoUrl ||
			JSON.stringify(formState.columns) !== JSON.stringify(initialState.columns)
		)
	}, [formState, initialState])

	return {
		formState,
		dispatch,
		isDirty,
		initialState,
	}
}

function RoadmapSettingsContent() {
	const { t } = useTranslation()
	const { currentProgram } = useProgramContext()
	const queryClient = useQueryClient()
	const { toast } = useToast()

	// Data fetching with improved error handling
	const {
		data: roadmap,
		isLoading: roadmapLoading,
		error: roadmapError,
		refetch: refetchRoadmap,
	} = useRoadmapData(currentProgram?.id)

	// Form state management
	const { formState, dispatch, isDirty } = useFormState(roadmap, roadmapLoading)

	// Navigation hooks
	const navigate = useNavigate()
	const location = useLocation()

	// Navigation blocking state
	const [showNavigationWarning, setShowNavigationWarning] = useState(false)
	const [pendingNavigation, setPendingNavigation] = useState<{
		path: string
	} | null>(null)

	// Handle browser navigation (refresh, close tab)
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (isDirty) {
				e.preventDefault()
				e.returnValue = ''
			}
		}

		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [isDirty])

	// Intercept navigation attempts when there are unsaved changes
	useEffect(() => {
		if (!isDirty) return

		// Override the navigate function to show warning first
		const handleNavigation = (e: MouseEvent) => {
			const target = e.target as HTMLElement
			const link = target.closest('a[href]') as HTMLAnchorElement

			if (link && link.href && !link.href.startsWith('#')) {
				const url = new URL(link.href)
				const path = url.pathname + url.search + url.hash

				// Check if it's an internal navigation
				if (
					url.origin === window.location.origin &&
					path !== location.pathname
				) {
					e.preventDefault()
					setPendingNavigation({ path })
					setShowNavigationWarning(true)
				}
			}
		}

		// Add click listener to intercept navigation
		document.addEventListener('click', handleNavigation, true)

		return () => {
			document.removeEventListener('click', handleNavigation, true)
		}
	}, [isDirty, location])

	const { data: slugValidation, isLoading: isSlugChecking } = useSlugValidation(
		formState.slug,
		roadmap?.id,
		t
	)

	const domain = import.meta.env.PROD ? 'feedbask.com' : 'localhost:8080'

	// Form update handlers
	const updateField = useCallback((field: keyof FormState, value: any) => {
		dispatch({ type: 'UPDATE_FIELD', field, value })
	}, [])

	const handleNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newName = e.target.value
			updateField('name', newName)

			// Auto-generate slug for new roadmaps
			if (!roadmap) {
				updateField('slug', generateSlug(newName))
			}
			dispatch({ type: 'SHOW_VALIDATION' })
		},
		[roadmap, updateField]
	)

	const handleSlugChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			updateField('slug', e.target.value)
			dispatch({ type: 'SHOW_VALIDATION' })
		},
		[updateField]
	)

	// Validation state logic
	const getValidationState = (): ValidationState => {
		if (!formState.showValidation) return 'idle'
		if (formState.slug.length > 0 && formState.slug.length < 3) return 'short'
		if (isSlugChecking) return 'checking'
		if (formState.slug.length >= 3 && slugValidation?.available) return 'valid'
		if (formState.slug.length >= 3 && slugValidation?.available === false)
			return 'invalid'
		return 'idle'
	}

	const validationState = getValidationState()

	// Mutations with better error handling
	const createMutation = useMutation({
		mutationFn: async (newRoadmap: {
			program_id: string
			name: string
			slug: string
			description?: string
		}) => {
			return await apiClient('/api/roadmaps', {
				method: 'POST',
				data: newRoadmap,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['roadmap', currentProgram?.id],
			})
			// Reset validation state after successful save
			dispatch({ type: 'RESET_VALIDATION' })
			toast({
				title: t('roadmapSettings.roadmapCreated'),
				description: t('roadmapSettings.roadmapCreatedSuccessfully'),
			})
		},
		onError: (error: any) => {
			console.error('Create roadmap error:', error)
			toast({
				title: t('roadmapSettings.errorCreatingRoadmap'),
				description: error.message || t('roadmapSettings.failedToCreateRoadmap'),
				variant: 'destructive',
			})
		},
	})

	const updateMutation = useMutation({
		mutationFn: async (updates: Partial<Roadmap>) => {
			if (!roadmap?.id) throw new Error('Roadmap ID is required')
			return await apiClient(`/api/roadmaps/${roadmap.id}`, {
				method: 'PUT',
				data: updates,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['roadmap', currentProgram?.id],
			})
			// Reset validation state after successful save
			dispatch({ type: 'RESET_VALIDATION' })
			toast({
				title: t('roadmapSettings.roadmapUpdated'),
				description: t('roadmapSettings.changesHaveBeenSaved'),
			})
		},
		onError: (error: any) => {
			console.error('Update roadmap error:', error)
			toast({
				title: t('roadmapSettings.errorUpdatingRoadmap'),
				description: error.message || t('roadmapSettings.failedToUpdateRoadmap'),
				variant: 'destructive',
			})
		},
	})

	const columnsMutation = useMutation({
		mutationFn: async (updatedColumns: Column[]) => {
			if (!roadmap?.id) throw new Error('Roadmap ID is required')

			const columnsForBackend = updatedColumns.map((col) => ({
				...(col.id.startsWith('temp-') ? {} : { id: col.id }),
				name: col.name,
				mapped_status: col.mapped_status,
				order_index: col.order_index,
				color: col.color,
			}))

			return await apiClient(`/api/roadmaps/${roadmap.id}/columns`, {
				method: 'PUT',
				data: columnsForBackend,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['roadmap', currentProgram?.id],
			})
		},
		onError: (error: any) => {
			console.error('Update columns error:', error)
			toast({
				title: t('roadmapSettings.errorUpdatingColumns'),
				description: error.message || t('roadmapSettings.failedToUpdateColumns'),
				variant: 'destructive',
			})
		},
	})

	// Save operation with better error handling
	const handleSave = useCallback(async () => {
		if (!currentProgram) {
			toast({
				title: t('roadmapSettings.error'),
				description: t('roadmapSettings.noProgramSelected'),
				variant: 'destructive',
			})
			return
		}

		try {
			if (roadmap) {
				// Update existing roadmap
				await updateMutation.mutateAsync({
					name: formState.name,
					slug: formState.slug,
					description: formState.description,
					is_public: formState.isPublic,
					logo_url: formState.logoUrl,
				})

				// Update columns if they've changed
				const columnsChanged =
					JSON.stringify(formState.columns) !==
					JSON.stringify(roadmap.roadmap_columns)
				if (columnsChanged) {
					await columnsMutation.mutateAsync(formState.columns)
				}
			} else {
				// Create new roadmap
				await createMutation.mutateAsync({
					program_id: currentProgram.id,
					name: formState.name,
					slug: formState.slug,
					description: formState.description,
				})
			}
		} catch (error) {
			console.error('Save operation failed:', error)
			// Error handling is done in mutation onError callbacks
		}
	}, [
		currentProgram,
		roadmap,
		formState,
		updateMutation,
		columnsMutation,
		createMutation,
		toast,
	])

	const handleTogglePublic = useCallback(
		async (checked: boolean) => {
		if (!roadmap?.id) {
			toast({
				title: t('roadmapSettings.error'),
				description: t('roadmapSettings.cannotChangeVisibility'),
				variant: 'destructive',
			})
			return
		}

		updateField('isPublic', checked)

		try {
			await updateMutation.mutateAsync({ is_public: checked })
			toast({
				title: t('roadmapSettings.visibilityUpdated'),
				description: t('roadmapSettings.roadmapIsNow', { 
					status: checked ? t('roadmapSettings.public') : t('roadmapSettings.private') 
				}),
			})
			} catch (error) {
				// Revert on error
				updateField('isPublic', !checked)
			}
		},
		[roadmap?.id, updateField, updateMutation, toast]
	)

	const handleAddColumn = useCallback(() => {
		const availableStatuses = [
			{ value: 'New' },
			{ value: 'Under Consideration' },
			{ value: 'Planned' },
			{ value: 'In Progress' },
			{ value: 'Launched' },
			{ value: 'Declined' },
		].filter(
			(status) =>
				!formState.columns.some((col) => col.mapped_status === status.value)
		)

		const defaultStatus = availableStatuses[0]?.value || 'New'
		const colors = [
			'#3B82F6',
			'#8B5CF6',
			'#10B981',
			'#F59E0B',
			'#EF4444',
			'#6366F1',
			'#EC4899',
			'#14B8A6',
		]
		const randomColor = colors[Math.floor(Math.random() * colors.length)]

		const newColumn: Column = {
			id: `temp-${uuidv4()}`,
			name: t('roadmapSettings.newColumn'),
			mapped_status: defaultStatus,
			order_index: formState.columns.length,
			color: randomColor,
		}

		updateField('columns', [...formState.columns, newColumn])
	}, [formState.columns, updateField])

	// Navigation warning handlers
	const handleNavigationConfirm = useCallback(() => {
		if (pendingNavigation) {
			// Navigate to the pending path
			navigate(pendingNavigation.path)
		}
		setShowNavigationWarning(false)
		setPendingNavigation(null)
	}, [pendingNavigation, navigate])

	const handleNavigationCancel = useCallback(() => {
		setShowNavigationWarning(false)
		setPendingNavigation(null)
	}, [])

	// Validation and save state
	const isSaveDisabled =
		updateMutation.isPending ||
		createMutation.isPending ||
		columnsMutation.isPending ||
		isSlugChecking ||
		!formState.name ||
		!formState.slug ||
		validationState === 'invalid' ||
		validationState === 'short' ||
		!isDirty

	const publicUrl = formState.slug
		? `${import.meta.env.PROD ? 'https' : 'http'}://${formState.slug}.${domain}`
		: ''

	// Loading state
	if (roadmapLoading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<div className="flex flex-col items-center gap-4">
					<div className="relative">
						<div className="w-12 h-12 border-muted rounded-full" />
						<Loader2 className="absolute inset-0 w-12 h-12 animate-spin text-primary" />
					</div>
				</div>
			</div>
		)
	}

	if (roadmapError) {
		return (
			<div className="max-w-4xl mx-auto p-6">
				<ErrorAlert error={roadmapError as Error} onRetry={refetchRoadmap} t={t} />
			</div>
		)
	}

	return (
		<div className="space-y-6 md:space-y-8 pb-20 px-4 md:px-0 animate-in fade-in-0 slide-in-from-bottom-4 duration-700 ease-out">
			{/* Roadmap visibility card */}
			<Card className="group relative overflow-hidden shadow-sm hover:shadow-md border transition-all duration-300 ease-out bg-gradient-to-br from-card via-card to-card/95">
				<div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 p-4 sm:p-6">
					<div className="flex items-start gap-4">
						<div
							className={cn(
								'flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-300 ease-out',
								formState.isPublic
									? 'bg-emerald-500/10 text-emerald-600 group-hover:bg-emerald-500/20'
									: 'bg-slate-500/10 text-slate-600 group-hover:bg-slate-500/20'
							)}>
							{formState.isPublic ? (
								<EyeIcon className="h-6 w-6" />
							) : (
								<Lock className="h-6 w-6" />
							)}
						</div>
						<div className="space-y-0">
							<div className="text-xl font-semibold tracking-tight">
								{t('roadmapSettings.roadmapVisibility')}
							</div>
							<div className="text-sm leading-relaxed text-slate-600">
								{t('roadmapSettings.yourRoadmapIs')}{' '}
								<span className="transition-colors duration-200 text-slate-600">
									{formState.isPublic
										? t('roadmapSettings.publicAndAccessibleToEveryone')
										: t('roadmapSettings.private')}
								</span>
							</div>
						</div>
					</div>
					<div className="flex items-center gap-3">
						<div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
							<span>{formState.isPublic ? t('roadmapSettings.public') : t('roadmapSettings.private')}</span>
						</div>
						<Switch
							checked={formState.isPublic}
							onCheckedChange={handleTogglePublic}
							disabled={!roadmap?.id || updateMutation.isPending}
							aria-label={t('roadmapSettings.toggleRoadmapVisibility')}
							className="data-[state=checked]:bg-emerald-500"
						/>
					</div>
				</div>
			</Card>

			{/* Basic Information Card */}
			<Card className="group relative overflow-hidden border shadow-sm hover:shadow-md transition-all duration-300 ease-out">
				<CardHeader className="relative space-y-1 pb-4 sm:pb-6 p-4 sm:p-6">
					<div className="flex items-center gap-3">
						<div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500/10 text-blue-600">
							<Eye className="h-5 w-5" />
						</div>
						<div>
							<CardTitle className="text-lg">{t('roadmapSettings.basicInformation')}</CardTitle>
							<CardDescription className="text-sm sm:text-base">
								{t('roadmapSettings.configureRoadmapDetails')}
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className="relative space-y-6 sm:space-y-8 p-4 sm:p-6">
					{formState.isPublic && roadmap && publicUrl && (
						<div className="animate-in slide-in-from-top-2 duration-300 ease-out">
							<PublicUrlDisplay url={publicUrl} />
						</div>
					)}
					{/* Name Field */}
					<div className="space-y-1 group/field">
						<Label
							htmlFor="roadmap-name"
							className="text-sm font-medium text-foreground transition-colors duration-200 group-focus-within/field:text-primary">
							{t('roadmapSettings.roadmapName')}
						</Label>
						<Input
							id="roadmap-name"
							value={formState.name}
							onChange={handleNameChange}
							placeholder={t('roadmapSettings.roadmapNamePlaceholder')}
							className="h-10 sm:h-12 text-sm sm:text-base transition-all duration-200 ease-out border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 hover:border-muted-foreground/40"
						/>
					</div>

					{/* Slug Field with Enhanced Validation */}
					<div className="space-y-1 group/field">
						<Label
							htmlFor="roadmap-slug"
							className="flex items-center gap-2 text-sm font-medium text-foreground transition-colors duration-200 group-focus-within/field:text-primary">
							{t('roadmapSettings.subdomain')}
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<div className="p-1 rounded-full hover:bg-muted transition-colors duration-200">
											<HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
										</div>
									</TooltipTrigger>
									<TooltipContent>
										<p>{t('roadmapSettings.createsUniqueSubdomain')}</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</Label>
						<div className="relative">
							<Input
								id="roadmap-slug"
								value={formState.slug}
								onChange={handleSlugChange}
								placeholder={t('roadmapSettings.subdomainPlaceholder')}
								className={cn(
									'h-10 sm:h-12 text-sm sm:text-base pr-12 transition-all duration-200 ease-out border-2 focus:ring-2 focus:ring-primary/20',
									validationState === 'invalid'
										? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/50'
										: validationState === 'valid'
										? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20 bg-emerald-50/50'
										: 'focus:border-primary hover:border-muted-foreground/40'
								)}
							/>
							<div className="absolute inset-y-0 right-3 flex items-center">
								<ValidationIcon state={validationState} />
							</div>
						</div>
						<div className="min-h-[24px] transition-all duration-200 ease-out">
							<ValidationMessage
								state={validationState}
								message={slugValidation?.message}
								t={t}
							/>
						</div>
					</div>

					{/* Logo Upload Section */}
					{roadmap && (
						<div className="space-y-1 pt-4 border-t">
							<Label className="text-sm font-medium text-foreground">
								{t('roadmapSettings.roadmapLogo')}
							</Label>
							<LogoUpload
								currentLogoUrl={formState.logoUrl}
								roadmapId={roadmap.id}
								onLogoChange={(newLogoUrl) => {
									updateField('logoUrl', newLogoUrl)
									queryClient.invalidateQueries({
										queryKey: ['roadmap', currentProgram?.id],
									})
								}}
								disabled={updateMutation.isPending || createMutation.isPending}
							/>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Roadmap Columns Card */}
			{roadmap && (
				<Card className="group relative overflow-hidden border shadow-sm hover:shadow-md transition-all duration-300 ease-out animate-in slide-in-from-bottom-4">
					<CardHeader className="relative flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0 pb-4 sm:pb-6 p-4 sm:p-6">
						<div className="flex items-center gap-3">
							<div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500/10 text-purple-600">
								<Plus className="h-5 w-5" />
							</div>
							<div>
								<CardTitle className="text-lg sm:text-xl font-semibold tracking-tight">
									{t('roadmapSettings.roadmapColumns')}
								</CardTitle>
								<CardDescription className="text-sm sm:text-base mt-1">
									{t('roadmapSettings.configureColumnsAndStatuses')}
								</CardDescription>
							</div>
						</div>
						<Button
							variant="outline"
							onClick={handleAddColumn}
							disabled={columnsMutation.isPending}
							className="h-10 px-4 w-full sm:w-auto transition-all duration-200 hover:bg-primary hover:text-primary-foreground hover:shadow-md hover:scale-105 disabled:hover:scale-100 disabled:hover:shadow-none">
							<Plus className="mr-2 h-4 w-4" />
							{t('roadmapSettings.addColumn')}
						</Button>
					</CardHeader>
					<CardContent className="relative p-4 sm:p-6">
						<div className="animate-in fade-in-0 duration-300 ease-out">
							<ColumnManager
								columns={formState.columns}
								onColumnsChange={(newColumns) =>
									updateField('columns', newColumns)
								}
								isSaving={columnsMutation.isPending}
							/>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Save Button Section */}
			<div className="flex justify-end pt-4 sm:pt-2 animate-in slide-in-from-bottom-2 duration-500 ease-out">
				<Button
					onClick={handleSave}
					disabled={isSaveDisabled}
					size="lg"
					className={cn(
						isDirty && !isSaveDisabled && 'ring-2 ring-primary/20'
					)}>
					{createMutation.isPending ||
					updateMutation.isPending ||
					columnsMutation.isPending ? (
						<div className="flex items-center gap-2">
							<Loader2 className="h-4 w-4 animate-spin" />
							<span>{t('roadmapSettings.saving')}</span>
						</div>
					) : (
						<span>{roadmap ? t('roadmapSettings.saveChanges') : t('roadmapSettings.createRoadmap')}</span>
					)}
				</Button>
			</div>

			{/* Navigation Warning Dialog */}
			<AlertDialog
				open={showNavigationWarning}
				onOpenChange={setShowNavigationWarning}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('roadmapSettings.unsavedChanges')}</AlertDialogTitle>
						<AlertDialogDescription>
							{t('roadmapSettings.unsavedChangesDescription')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={handleNavigationCancel}>
							{t('roadmapSettings.stayOnPage')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleNavigationConfirm}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
							{t('roadmapSettings.leaveWithoutSaving')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}

// Export wrapper with DataLoadingGuard
export function RoadmapSettings() {
	return (
		<DataLoadingGuard requireProgram={true}>
			<RoadmapSettingsContent />
		</DataLoadingGuard>
	)
}
